import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Leaf, LayoutGrid, BarChart3, Flag, MessageCircle,
  Store, UserCircle, Bell, ChevronRight, Watch, Shield, Trophy, Crown, BookOpen, Video, Newspaper
} from 'lucide-react';
import { COLORS, TIER_NAMES, TIER_COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import GuidedTour from './GuidedTour';
import WhatsNewBanner from './WhatsNewBanner';
import TokenRateTicker from './TokenRateTicker';

const TAB_ITEMS = [
  { name: '', title: 'Home', icon: LayoutGrid },
  { name: 'insights', title: 'Insights', icon: BarChart3 },
  { name: 'goals', title: 'Goals', icon: Flag },
  { name: 'challenges', title: 'Challenges', icon: Trophy },
  { name: 'library', title: 'Library', icon: BookOpen },
  { name: 'blog', title: 'Blog', icon: Newspaper },
  { name: 'seminars', title: 'Seminars', icon: Video },
  { name: 'wearables', title: 'Wearables', icon: Watch },
  { name: 'chat', title: 'AI Coach', icon: MessageCircle },
  { name: 'store', title: 'Store', icon: Store },
  { name: 'notifications', title: 'Alerts', icon: Bell },
  { name: 'profile', title: 'Profile', icon: UserCircle },
];

const ADMIN_TAB = { name: 'admin', title: 'Admin', icon: Shield };

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1080);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1080);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user && !user.tour_completed) {
      setShowTour(true);
    }
  }, [user]);

  const currentTab = location.pathname.replace('/dashboard/', '').replace('/dashboard', '');

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: COLORS.background }}>
      {showTour && <GuidedTour onComplete={() => setShowTour(false)} />}
      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside style={{
          width: 240, backgroundColor: COLORS.surface, borderRight: `1px solid ${COLORS.border}`,
          display: 'flex', flexDirection: 'column', padding: '20px 16px', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px', marginBottom: 8 }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <Leaf size={24} color={COLORS.primary} />
              <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.textPrimary, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif' }}>ThriveLonger.ai</span>
              <img src="/thrivelonger-logo.png" alt="ThriveLonger logo" data-testid="brand-logo-sidebar" style={{ height: 28, width: 'auto', display: 'block' }} />
            </Link>
            <button style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', position: 'relative' }}>
              <Bell size={22} color={COLORS.textPrimary} />
              <div style={{ position: 'absolute', top: 2, right: 2, minWidth: 18, height: 18, borderRadius: 9, backgroundColor: COLORS.error, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#FFF' }}>3</span>
              </div>
            </button>
          </div>

          <div style={{ height: 1, backgroundColor: COLORS.border, margin: '16px 0' }} />

          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[...TAB_ITEMS, ...(user?.role === 'admin' ? [ADMIN_TAB] : [])].map(item => {
              const active = currentTab === item.name;
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={`/dashboard${item.name ? '/' + item.name : ''}`}
                  className="sidebar-nav-item"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 10,
                    textDecoration: 'none', backgroundColor: active ? `${COLORS.primary}12` : 'transparent',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <Icon size={20} color={active ? COLORS.primary : COLORS.textSecondary} />
                  <span style={{ fontSize: 14, fontWeight: active ? 600 : 500, color: active ? COLORS.primary : COLORS.textSecondary }}>{item.title}</span>
                </Link>
              );
            })}
          </nav>

          <div style={{ paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 11, color: COLORS.border, display: 'block', textAlign: 'center' }}>&copy; 2026 ThriveLonger.ai. All Rights Reserved.</span>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TokenRateTicker />
        {/* Mobile Header */}
        {isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', backgroundColor: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Leaf size={20} color={COLORS.primary} />
              <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>ThriveLonger.ai</span>
              <img src="/thrivelonger-logo.png" alt="ThriveLonger logo" data-testid="brand-logo-mobile" style={{ height: 24, width: 'auto', display: 'block' }} />
            </div>
            <button style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', position: 'relative' }}>
              <Bell size={22} color={COLORS.textPrimary} />
              <div style={{ position: 'absolute', top: 2, right: 2, minWidth: 18, height: 18, borderRadius: 9, backgroundColor: COLORS.error, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#FFF' }}>3</span>
              </div>
            </button>
          </div>
        )}

        <div className="dashboard-content" style={{ flex: 1, overflowY: 'auto', paddingBottom: isMobile ? 72 : 0 }}>
          <WhatsNewBanner />
          <Outlet />
        </div>

        {/* Mobile Bottom Nav */}
        {isMobile && (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex',
            backgroundColor: COLORS.surface, borderTop: `1px solid ${COLORS.border}`,
            padding: '8px 0 8px', zIndex: 50,
          }}>
            {[...TAB_ITEMS, ...(user?.role === 'admin' ? [ADMIN_TAB] : [])].map(item => {
              const active = currentTab === item.name;
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={`/dashboard${item.name ? '/' + item.name : ''}`}
                  className="bottom-nav-item"
                  style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    padding: '4px 0', textDecoration: 'none',
                  }}
                >
                  <Icon size={22} color={active ? COLORS.primary : COLORS.textSecondary} strokeWidth={active ? 2.5 : 2} />
                  <span style={{ fontSize: 10, fontWeight: active ? 600 : 500, color: active ? COLORS.primary : COLORS.textSecondary }}>{item.title}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
